import { useEffect, useRef } from 'react';
import { Outlet } from 'react-router-dom';
import { toast } from 'react-toastify';

import { ACHIEVEMENTS } from '../../lib/idle/gameConfig';
import { formatCoins } from '../../lib/idle/format';
import { claimOffline, useIdle } from '../../lib/idle/idleStore';
import { usePremium, useSession } from '../../lib/session';
import { useUI } from '../../lib/ui';
import '../Idle/homely.css';
import GuestPrompt from '../Idle/GuestPrompt';
import Sidebar from '../Sidebar/Sidebar';
import UpgradeModal from '../Sidebar/UpgradeModal';
import GameHeader from './GameHeader';
import Styles from './GameShell.module.css';

const GameShell = () => {
  const ready = useSession((s) => s.ready);
  const premium = usePremium();
  const theme = useIdle((s) => s.settings.theme);
  const unlockedIds = useIdle((s) =>
    ACHIEVEMENTS.filter((a) => a.test(s)).map((a) => a.id).join(','),
  );
  const pricing = useUI((s) => s.pricing);
  const closePricing = useUI((s) => s.closePricing);

  // Gem cosmetics: swap the paper
  useEffect(() => {
    document.documentElement.dataset.hmTheme = theme;
    return () => {
      delete document.documentElement.dataset.hmTheme;
    };
  }, [theme]);

  // Offline earnings: 2 hours for free accounts, 24 for Premium
  useEffect(() => {
    if (!ready) return;
    const r = claimOffline(premium);
    if (r && r.gain >= 1) {
      const upsell = r.capped && !premium ? ' Free accounts bank 2 hours. Premium banks 24.' : '';
      toast(`Welcome back! Your band earned ${formatCoins(r.gain)} coins while you were away.${upsell}`, {
        position: 'bottom-center',
        autoClose: 6000,
      });
    }
  }, [ready]);

  // A small toast when an achievement flips on
  const seen = useRef(null);
  useEffect(() => {
    const ids = unlockedIds ? unlockedIds.split(',') : [];
    if (seen.current === null) {
      seen.current = new Set(ids);
      return;
    }
    ids.forEach((id) => {
      if (!seen.current.has(id)) {
        seen.current.add(id);
        const a = ACHIEVEMENTS.find((x) => x.id === id);
        // bottom-right: bottom-left would land on top of the sidebar's helpers panel
        toast(`Achievement: ${a.name}`, { position: 'bottom-right', autoClose: 3500 });
      }
    });
  }, [unlockedIds]);

  return (
    <div className={Styles.app}>
      <Sidebar />
      <div className={Styles.main}>
        <GameHeader />
        <div className={Styles.content}>
          <Outlet />
        </div>
      </div>

      {pricing && <UpgradeModal reason={pricing.reason} onClose={closePricing} />}
      <GuestPrompt />
    </div>
  );
};

export default GameShell;